"use client"
import styled from "@emotion/styled"
import DiscountIcon from "@eurodrive/ui/icons/DiscountIcon"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { CarTypeLabels, CarTypeOrder } from "@eurodrive/utils"
import { Tab, Tabs, Typography } from "@mui/material"
import BudgetSection from "./Filters/BudgetSection"
import CarFeaturesSection from "./Filters/CarFeaturesSection"
import CarTypeSection from "./Filters/CarTypeSection"
import DealTypeSection from "./Filters/DealTypeSection"
import InputGroup from "./Filters/InputGroup"
import ManufacturerModelSection from "./Filters/ManufacturerModelSection"
import { SearchFilters, SearchFiltersActions } from "./Filters/useSearchFilters"

const StyledLayout = styled.div<{ isMobile?: boolean }>`
  padding-top: ${(props) => (props.isMobile ? "24px" : "64px")};
  padding-right: ${(props) => (props.isMobile ? "16px" : "24px")};
  padding-left: ${(props) => (props.isMobile ? "16px" : "0")};
  border-right: ${(props) => (props.isMobile ? "none" : "1px solid #aaa")};
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledTabs = styled(Tabs)`
  display: flex;
  background-color: ${sharedColors.lightGrey};
  border-radius: 8px;
  padding: 2px;
`

const StyledTab = styled(Tab)`
  &.MuiTab-root {
    width: 100%;
    padding: 8px 16px;
    min-width: 0;
    font-size: 16px;
    line-height: 140%;
    text-transform: none;
  }
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledDiscountChip = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${(props) => (props.active ? "#313237" : "#F3F3F3")};
  color: ${(props) => (props.active ? "#FFFFFF" : "#000000")};
  font-family: "Assistant", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  border: none;
  cursor: pointer;
  width: fit-content;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.active ? "#3d3d42" : "#e8e8e8")};
  }

  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }

  svg {
    width: 16px;
    height: 16px;
    path,
    circle {
      fill: ${(props) => (props.active ? "#FFFFFF" : "#000000")};
    }
    path[stroke],
    path[stroke-opacity] {
      stroke: ${(props) => (props.active ? "#FFFFFF" : "#000000")};
    }
  }
`

interface FiltersProps {
  filters: SearchFilters
  actions: SearchFiltersActions
  carFeatures: Array<{ id: string; label: string }>
  carTypes: Array<{ id: string; label: string; icon: string }>
  manufacturers: Array<{
    value: string
    label: string
    models: Array<{ value: string; label: string }>
  }>
  isMobile?: boolean
  minPrice: number
  maxPrice: number
}

export default function Filters({
  filters,
  actions,
  carFeatures,
  carTypes,
  manufacturers,
  isMobile = false,
  minPrice,
  maxPrice,
}: FiltersProps) {
  function numberFormat(value: number) {
    return (
      new Intl.NumberFormat("he-IL", {
        style: "decimal",
      }).format(value) + " ש״ח"
    )
  }

  return (
    <StyledLayout isMobile={isMobile}>
      <Typography variant="h3" component="h1">
        סינון רכבים
      </Typography>

      {/* Discount Filter Chip */}
      <StyledDiscountChip
        active={filters.showDiscountsOnly}
        onClick={() => actions.setShowDiscountsOnly(!filters.showDiscountsOnly)}
        aria-pressed={filters.showDiscountsOnly}
        aria-label={
          filters.showDiscountsOnly ? "הסר סינון מבצעים" : "הצג רק מבצעים"
        }
      >
        <DiscountIcon />
        מבצעים
      </StyledDiscountChip>

      <StyledTabs
        value={filters.carType}
        onChange={(e, newValue) => actions.setCarType(newValue)}
        aria-label="בחר דרגת רכב"
      >
        {CarTypeOrder.map((carType) => (
          <StyledTab
            key={carType}
            label={CarTypeLabels[carType]}
            value={carType}
          />
        ))}
      </StyledTabs>

      {/* Deal Type Section */}
      <DealTypeSection
        dealType={filters.dealType}
        handleDealTypeChange={actions.setDealType}
      />

      {/* Car Features Section */}
      <CarFeaturesSection
        selectedFeatures={filters.selectedFeatures}
        toggleFeature={actions.toggleFeature}
        carFeatures={carFeatures}
      />

      {/* Car Types Section */}
      <CarTypeSection
        selectedCarTypes={filters.selectedCarTypes}
        toggleCarType={actions.toggleCarType}
        carTypes={carTypes}
      />

      {/* Budget Section */}
      <BudgetSection
        priceRange={filters.priceRange}
        handleSliderChange={actions.handleSliderChange}
        numberFormat={numberFormat}
        minPrice={minPrice}
        maxPrice={maxPrice}
      />

      {/* Manufacturer and Model Section */}
      <ManufacturerModelSection
        selectedManufacturer={filters.selectedManufacturer}
        selectedModel={filters.selectedModel}
        handleManufacturerChange={actions.handleManufacturerChange}
        handleModelChange={actions.handleModelChange}
        manufacturers={manufacturers}
      />

      {/* Year Input Group - Only show for used cars */}
      {filters.dealType === "used" && (
        <InputGroup
          title="שנת ייצור"
          fromLabel="משנת"
          toLabel="עד שנת"
          fromPlaceholder="מינימום"
          toPlaceholder="2025"
          fromValue={filters.yearFrom}
          toValue={filters.yearTo}
          onFromChange={(e) => actions.setYearFrom(e.target.value)}
          onToChange={(e) => actions.setYearTo(e.target.value)}
          type="number"
        />
      )}

      {/* KM Input Group - Only show for used cars */}
      {filters.dealType === "used" && (
        <InputGroup
          title="ק״מ"
          fromLabel="מ"
          toLabel="עד"
          fromPlaceholder="מינימום"
          toPlaceholder="20,0000"
          fromValue={filters.kmFrom}
          toValue={filters.kmTo}
          onFromChange={(e) => actions.setKmFrom(e.target.value)}
          onToChange={(e) => actions.setKmTo(e.target.value)}
          type="number"
        />
      )}
    </StyledLayout>
  )
}
